import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MODUL_KEZDOLAP } from '../../modul-tema';
import { DataService } from 'src/app/data.service';
import { modeNev } from 'src/app/mode-nevek';
import { AdminMenuPont, AdminMenuService, AdminMenuTerulet } from '../admin-menu.service';
import { keretVilagos } from '../../szinmod';

/**
 *  ADMIN KERET – KEZELŐKÖZPONT (2026-10-16).
 *
 *  A felső, három sorba tördelődő gombsor (13 csoport legördülővel) helyett:
 *   - BAL OLDALT A TERÜLETEK (admin-katalogus.ts › adminTeruletek; SULINFORM-ban 8), a régi gombok
 *     ikonjaival; a kattintás a kezdőlapon nyitja meg a terület pontjait;
 *   - FELÜL KERESŐ minden látható pontra (cím, leírás, régi menünév), Ctrl+K-ra is;
 *   - A FEJLÉCBEN VISSZA GOMB és profil-lenyíló: az adminban nincs bal sáv (app.component ›
 *     menuRejtve, a user: „különben nem fogunk elférni”), a vissza oda visz, ahonnan beléptünk.
 *
 *  A láthatóság szabálya a régi (AdminMenuService): a jogkörszerkesztő, a SULINFORM-on kívül a
 *  kiszolgáló admin-döntése, a törzsadat-táblák a Törzsadatok jogával.
 */
@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
})
export class AdminLayoutComponent implements OnInit, OnDestroy {

  @ViewChild('keresoMezo') keresoMezo?: ElementRef<HTMLInputElement>;

  /** Az aktuális modul (alrendszer). */
  mode = 'SULINFORM';
  get sulinform(): boolean { return this.mode === 'SULINFORM'; }
  get modNev(): string { return modeNev(this.mode); }

  /** A SULINFORM Fókuszában világos-e a fejléc (szinmod.ts) – a profil-lenyíló ehhez igazodik. */
  get keretVilagos(): boolean { return keretVilagos(); }

  teruletek: AdminMenuTerulet[] = [];
  /** Betöltődött-e a jogosultság (addig nem írjuk ki, hogy „nincs elérhető képernyő"). */
  kesz = false;
  /** A kiemelt terület (a kezdőlapon a választott, máshol az aktuális pont területe). */
  aktivTerulet = '';
  /** Az aktuális pont (morzsához). */
  aktivPont: AdminMenuPont | null = null;
  kezdolapon = false;

  q = '';
  talalatok: AdminMenuPont[] = [];
  kijelolt = 0;

  sajatIntezmeny: any;

  constructor(private data: DataService, private router: Router, private menu: AdminMenuService) { }

  /** Vissza: oda, ahonnan az adminba léptünk; ha nincs ilyen, a modul kezdőlapjára. */
  vissza() {
    let honnan = '';
    try { honnan = sessionStorage.getItem('admin-elotti') || ''; } catch { /* ignore */ }
    this.router.navigateByUrl(honnan || MODUL_KEZDOLAP[this.mode] || '/home');
  }

  // Munkatanév-jelzés: ha az admin egy előkészítés alatt álló tanéven dolgozik,
  // ezt minden admin felületen látnia kell (különben észrevétlenül rossz évbe ment).
  // Csak az iskolai modulban értelmes – a tanév a SULINFORM fogalma.
  munkatanev: number | null = null;
  aktualistanev: number | null = null;
  tanevNevek: { [ev: number]: string } = {};

  get munkatanevEltero(): boolean {
    return this.sulinform && this.munkatanev != null && this.aktualistanev != null
      && Number(this.munkatanev) !== Number(this.aktualistanev);
  }
  tanevNev(ev: number | null): string {
    if (ev == null) return '—';
    return this.tanevNevek[ev] || String(ev);
  }
  async munkatanevVissza() {
    await this.data.setMunkatanev(this.aktualistanev);
    this.munkatanev = this.aktualistanev;
    window.location.reload();
  }

  private async munkatanevBetolt() {
    this.munkatanev = await this.data.getMunkatanev();
    this.aktualistanev = await this.data.getAktualisTanev();
    const intezmenyId = String(this.sajatIntezmeny?.intezmenyId || '').trim();
    if (!intezmenyId) return;
    this.data.getTanevLista(intezmenyId).subscribe({
      next: (r: any) => {
        this.aktualistanev = r?.aktualistanev ?? this.aktualistanev;
        for (const t of (r?.tanevek || [])) this.tanevNevek[t.id] = t.name;
      },
      error: () => { /* a jelzés enélkül is működik az évszámmal */ },
    });
  }

  private subs: Subscription[] = [];

  async ngOnInit() {
    this.mode = String((await this.data.getFromStorage('currentMode')) || 'SULINFORM');
    this.sajatIntezmeny = (await this.data.getFromStorage('sajatIntezmeny'));
    try {
      this.teruletek = await this.menu.teruletek(true);
    } catch {
      this.teruletek = [];
    }
    this.kesz = true;

    if (this.sulinform) {
      this.munkatanevBetolt();
      this.subs.push(this.data.munkatanev$.subscribe((ev) => { if (ev !== null) this.munkatanev = ev; }));
    }
    this.urlbolAllit(this.router.url);
    this.subs.push(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.urlbolAllit(event.urlAfterRedirects);
    }));
  }

  ngOnDestroy() {
    for (const s of this.subs) s.unsubscribe();
  }

  /** A címsorból: melyik terület és pont aktív; a megnyitott pont a „gyakran használt”-ba számít. */
  private urlbolAllit(rawUrl: string) {
    const url = String(rawUrl || '').split('#')[0];
    this.kezdolapon = /^\/admin(\/kezdolap)?(\?|$)/.test(url);
    if (this.kezdolapon) {
      const m = url.match(/[?&]terulet=([^&]+)/);
      this.aktivTerulet = (m && decodeURIComponent(m[1])) || this.menu.utolsoTerulet() || this.teruletek[0]?.kulcs || '';
      this.aktivPont = null;
      return;
    }
    const pont = this.menu.pontUrlbol(this.teruletek, url);
    this.aktivPont = pont;
    if (pont) {
      this.aktivTerulet = pont.terulet;
      this.menu.rogzitHasznalat(pont.ut);
    }
  }

  teruletNyit(t: AdminMenuTerulet) { this.teruletNyitKulcs(t.kulcs); }

  teruletNyitKulcs(kulcs: string) {
    this.menu.mentTerulet(kulcs);
    this.router.navigate(['/admin/kezdolap'], { queryParams: { terulet: kulcs } });
  }

  // ── kereső ──────────────────────────────────────────────────────────────────────────────────
  keres() {
    this.talalatok = this.menu.keres(this.teruletek, this.q);
    this.kijelolt = 0;
  }

  nyit(pt: AdminMenuPont) {
    this.q = '';
    this.talalatok = [];
    this.router.navigateByUrl(pt.ut);
  }

  keresoBillentyu(ev: KeyboardEvent) {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); this.kijelolt = Math.min(this.kijelolt + 1, this.talalatok.length - 1); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); this.kijelolt = Math.max(this.kijelolt - 1, 0); }
    else if (ev.key === 'Enter') { const pt = this.talalatok[this.kijelolt]; if (pt) this.nyit(pt); }
    else if (ev.key === 'Escape') { this.q = ''; this.talalatok = []; }
  }

  /** Ctrl+K (Macen Cmd+K): a kereső bárhonnan az adminban. */
  @HostListener('document:keydown', ['$event'])
  gyorsbillentyu(ev: KeyboardEvent) {
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'k' || ev.key === 'K')) {
      ev.preventDefault();
      this.keresoMezo?.nativeElement.focus();
    }
  }

  trackTerulet(_i: number, t: AdminMenuTerulet) { return t.kulcs; }
  trackPont(_i: number, p: AdminMenuPont) { return p.ut; }
}
